import { useState } from 'react';
import { Button, Group, Select, TextInput } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { Search, RotateCcw } from 'lucide-react';
import { toIsoDate } from '@atlas/shared-utils';

export interface DateRangeFilterSelectOption {
  value: string;
  label: string;
}

export interface DateRangeFilterField {
  key: string;
  label: string;
  placeholder?: string;
  icon?: 'search';
  flex?: string;
  type?: 'text' | 'select';
  options?: DateRangeFilterSelectOption[];
}

export interface DateRangeFilterValues {
  startDate: string;
  endDate: string;
  [key: string]: string | undefined;
}

interface DateRangeFilterProps {
  onApply: (values: DateRangeFilterValues) => void;
  onReset?: () => void;
  extraFields?: DateRangeFilterField[];
  defaultStartDate?: Date;
  defaultEndDate?: Date;
  applyLabel?: string;
  resetLabel?: string;
}

// Mantine 8's DatePickerInput emits/consumes ISO `string | null` rather than
// `Date | null` (the v7 shape). We keep the public API on `Date` for backward
// compat and convert at the boundary via the shared `toIsoDate`.
export function DateRangeFilter({
  onApply,
  onReset,
  extraFields = [],
  defaultStartDate,
  defaultEndDate,
  applyLabel = 'Apply',
  resetLabel = 'Reset',
}: DateRangeFilterProps) {
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const [startDate, setStartDate] = useState<string | null>(toIsoDate(defaultStartDate ?? firstOfMonth));
  const [endDate, setEndDate] = useState<string | null>(toIsoDate(defaultEndDate ?? today));
  const [extraValues, setExtraValues] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    for (const field of extraFields) {
      initial[field.key] = '';
    }
    return initial;
  });

  const handleApply = () => {
    if (!startDate || !endDate) return;
    const values: DateRangeFilterValues = {
      startDate,
      endDate,
    };
    for (const field of extraFields) {
      const val = extraValues[field.key]?.trim();
      if (val) values[field.key] = val;
    }
    onApply(values);
  };

  const handleReset = () => {
    const resetStart = toIsoDate(defaultStartDate ?? firstOfMonth);
    const resetEnd = toIsoDate(defaultEndDate ?? today);
    setStartDate(resetStart);
    setEndDate(resetEnd);
    const cleared: Record<string, string> = {};
    for (const field of extraFields) {
      cleared[field.key] = '';
    }
    setExtraValues(cleared);

    if (onReset) {
      onReset();
    } else {
      onApply({
        startDate: resetStart,
        endDate: resetEnd,
      });
    }
  };

  return (
    <Group wrap="wrap" gap="sm" align="flex-end">
      <DatePickerInput
        label="Start Date"
        value={startDate}
        onChange={setStartDate}
        maxDate={endDate ?? undefined}
        style={{ flex: '0 1 160px' }}
      />
      <DatePickerInput
        label="End Date"
        value={endDate}
        onChange={setEndDate}
        minDate={startDate ?? undefined}
        style={{ flex: '0 1 160px' }}
      />
      {extraFields.map((field) =>
        field.type === 'select' ? (
          <Select
            key={field.key}
            label={field.label}
            placeholder={field.placeholder}
            data={field.options ?? []}
            value={extraValues[field.key] || null}
            onChange={(val) =>
              setExtraValues((prev) => ({ ...prev, [field.key]: val ?? '' }))
            }
            clearable
            searchable
            style={{ flex: field.flex ?? '1 1 160px' }}
          />
        ) : (
          <TextInput
            key={field.key}
            label={field.label}
            placeholder={field.placeholder}
            leftSection={field.icon === 'search' ? <Search size={14} /> : undefined}
            leftSectionPointerEvents="none"
            value={extraValues[field.key] ?? ''}
            onChange={(e) => {
              const val = e.currentTarget.value;
              setExtraValues((prev) => ({ ...prev, [field.key]: val }));
            }}
            style={{ flex: field.flex ?? '1 1 160px' }}
          />
        )
      )}
      <Button onClick={handleApply} disabled={!startDate || !endDate}>
        {applyLabel}
      </Button>
      <Button variant="outline" leftSection={<RotateCcw size={14} />} onClick={handleReset}>
        {resetLabel}
      </Button>
    </Group>
  );
}
